import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, browserSessionPersistence } from 'firebase/auth';
import { getFirestore, doc, getDocFromServer, runTransaction, setDoc } from 'firebase/firestore';
import { getStorage, uploadBytes, uploadBytesResumable, getDownloadURL as firebaseGetDownloadURL, ref as storageRef } from 'firebase/storage';
import { applyAuthConfigBranding } from './preAuthBranding';

// Safe env accessor that works across Vite client and Node.js environments
const getEnvVar = (key: string): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
    return import.meta.env[key] as string;
  }
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    return process.env[key] as string;
  }
  return '';
};

// Action code settings for Email Link (magic link) sign-in.
// The URL must match an allowed domain in Firebase Console > Authentication > Settings.
const authDomain = getEnvVar('VITE_FIREBASE_AUTH_DOMAIN');
export const emailLinkActionCodeSettings = {
  url: authDomain
    ? `https://${authDomain}/finishSignUp`
    : 'http://localhost:5173/finishSignUp',
  handleCodeInApp: true,
};

// Enforce required env vars in browser client mode while allowing safe SSR/Node evaluation
if (typeof window !== 'undefined') {
  const requiredEnvVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
    'VITE_FIREBASE_FIRESTORE_DB_ID',
  ] as const;
  for (const key of requiredEnvVars) {
    if (!getEnvVar(key)) {
      console.warn(`[Firebase] Missing env var: ${key}. Set it in .env.local or deployment variables.`);
    }
  }
}

export const firebaseConfig = {
  apiKey: getEnvVar('VITE_FIREBASE_API_KEY'),
  authDomain: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: getEnvVar('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getEnvVar('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getEnvVar('VITE_FIREBASE_APP_ID'),
  firestoreDatabaseId: getEnvVar('VITE_FIREBASE_FIRESTORE_DB_ID'),
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);

// L3: a simulation tab must hold its session in per-tab session
// persistence WITHOUT ever touching the admin tab's locally persisted
// session. Selecting the persistence at initializeAuth time is the
// only safe point: calling setPersistence() on an Auth instance that
// already loaded the shared local record MIGRATES that record —
// PersistenceUserManager.setPersistence reads the current user from
// the old persistence, removes it there, and rewrites it under the
// new one (firebase/auth 12.x) — so the previous flow (getAuth with
// the default local persistence, then setPersistence to session in
// the sim tab) deleted the admin's stored session from the shared
// per-origin store, and the admin's own tab was signed out. With the
// persistence chosen here, the sim tab's Auth never reads or removes
// the local record at all.
const isSimulatingBoot =
  typeof window !== 'undefined' && window.location.search.includes('simToken=');
export const auth = isSimulatingBoot
  ? initializeAuth(app, { persistence: browserSessionPersistence })
  : getAuth(app);
export const storage = getStorage(app);

/**
 * Synchronize client Firebase Auth instance with active host workspace tenantId.
 */
export async function syncWorkspaceTenantAuth(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  try {
    const res = await fetch('/api/workspace/auth-config');
    if (res.ok) {
      const config = await res.json();
      // White-label Phase 1: the same payload carries the host workspace's
      // branding — hand it to the pre-auth branding store so sign-in/sign-up
      // render the workspace's brand instead of the build-time default.
      // A non-OK response or fetch failure simply skips this, leaving the
      // build-time defaults in place (the fallback contract).
      //
      // Self-Serve Onboarding §2.2 T-6: the platform signup route
      // (/get-started) is EXCLUDED from this merge — it renders the
      // platform (EduTrouve) brand and must never wear the host
      // workspace's brand (on workspace-registered hosts the auth-config
      // payload is that workspace's, e.g. ISATS on pre-dev).
      const onPlatformSignupRoute = typeof window !== 'undefined' && window.location.pathname === '/get-started';
      if (!onPlatformSignupRoute) {
        applyAuthConfigBranding(config);
      }
      if (config.tenantId) {
        auth.tenantId = config.tenantId;
      } else {
        auth.tenantId = null;
      }
      return config.workspaceId || null;
    }
  } catch (e) {
    console.warn('[Firebase Auth] Failed to fetch workspace auth-config:', e);
  }
  return null;
}

// ── Auth persistence & tenant sync: must resolve before auth state listener fires ──
// Persistence is selected at initializeAuth time above (see the L3
// note): a normal boot uses getAuth's default local persistence, a
// simulation boot uses per-tab session persistence. Nothing here may
// call setPersistence on a live Auth instance — the migration it
// performs is what signed admins out when a simulation tab opened.
export const authReady: Promise<void> = (async () => {
  await syncWorkspaceTenantAuth();
})();

export const BUCKET_URL = `gs://${firebaseConfig.storageBucket}`;

export async function getDownloadURL(storageRef: any): Promise<string> {
  if (storageRef && typeof storageRef === 'object' && '_fallbackUrl' in storageRef) {
    return storageRef._fallbackUrl;
  }
  return firebaseGetDownloadURL(storageRef);
}

export function isFirebaseStorageReference(value?: string | null): boolean {
  return !!value && (value.startsWith('gs://') || (!value.startsWith('http') && !value.startsWith('blob:') && !value.startsWith('data:')));
}

/**
 * Resolves a document URL for use in the viewer modal.
 *
 * FIX (2026-06-23): Previously this function short-circuited and returned the
 * raw `downloadUrl` string without validation. Firebase Storage signed URLs
 * expire after a period and the raw gs:// storagePath was sometimes stored
 * directly as `downloadUrl` by older upload flows, causing the viewer to
 * receive a non-HTTP reference it could not embed.
 *
 * Resolution priority:
 *   1. If storagePath is a valid Firebase Storage reference (gs:// or relative
 *      path), ALWAYS fetch a fresh signed HTTPS URL from Storage — this is the
 *      only authoritative, non-expiring-aware source.
 *   2. Fall back to downloadUrl only if storagePath is absent or already an
 *      HTTP URL (i.e. not a Storage reference that needs signing).
 *   3. Final fallback: return storagePath as-is for plain HTTP paths.
 *
 * This ensures PDFs, DOCs, and images always receive a valid signed HTTPS URL
 * regardless of how or when the record was originally written.
 */
export async function resolveFirebaseDocumentUrl(
  storagePath?: string | null,
  downloadUrl?: string | null
): Promise<string> {
  // Priority 1: always re-derive a fresh signed URL when we have a Storage path
  if (storagePath && isFirebaseStorageReference(storagePath)) {
    try {
      return await firebaseGetDownloadURL(storageRef(storage, storagePath));
    } catch (err) {
      console.warn('[resolveFirebaseDocumentUrl] Failed to get signed URL from storagePath, falling back:', storagePath, err);
      // Fall through to downloadUrl fallback below
    }
  }

  // Priority 2: use downloadUrl only if it is a proper HTTP(S) URL
  if (downloadUrl && (downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://'))) {
    return downloadUrl;
  }

  // Priority 3: storagePath that is already an HTTP URL (edge case)
  if (storagePath && (storagePath.startsWith('http://') || storagePath.startsWith('https://'))) {
    return storagePath;
  }

  // Nothing usable
  return '';
}

/**
 * Determines the safest URL to use inside an <iframe> or <img> viewer.
 *
 * Google Docs viewer requires a plain HTTPS URL. Firebase signed URLs contain
 * query params that are valid but must be fully encoded when passed to the
 * Docs viewer. This helper returns { url, isGoogleViewer } so callers can
 * branch on render strategy.
 */
export function resolveDocumentViewUrl(
  rawUrl: string,
  fileHint: string
): { url: string; isGoogleViewer: boolean } {
  const lower = `${fileHint} ${rawUrl.split('?')[0]}`.toLowerCase();
  const canOfficePreview =
    /\.(doc|docx|xls|xlsx|ppt|pptx|csv|rtf|txt)/i.test(lower) ||
    /word|excel|spreadsheet|presentation|text\/plain/.test(lower);

  if (canOfficePreview && rawUrl.startsWith('https://')) {
    return {
      url: `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(rawUrl)}`,
      isGoogleViewer: true,
    };
  }

  return { url: rawUrl, isGoogleViewer: false };
}

export function getUploadedStoragePath(snapshot: any, fallbackPath?: string): string {
  return snapshot?.ref?.fullPath || fallbackPath || snapshot?.metadata?.fullPath || '';
}

export function getStorageErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const fbErr = error as { code: string; message: string };
    switch (fbErr.code) {
      case 'storage/unauthorized': return "You don't have permission to upload this file";
      case 'storage/canceled': return 'Upload was cancelled';
      case 'storage/unknown': return 'Upload failed. Please check your connection and try again';
      case 'permission-denied': return 'Upload failed: Missing or insufficient permissions. Please try again or contact support.';
      case 'PERMISSION_DENIED': return 'Upload failed: Missing or insufficient permissions. Please try again or contact support.';
      default: return `Upload failed: ${fbErr.message}`;
    }
  }
  return error instanceof Error ? error.message : 'Upload failed. Please try again.';
}

export function buildStoragePath(path: string): string {
  if (path.startsWith('gs://')) return path;
  return `${BUCKET_URL}/${path}`;
}

export async function uploadBytesResumableWithProgress(
  storageRef: any,
  file: Blob | Uint8Array | ArrayBuffer | File,
  onProgress?: (progress: number) => void,
  timeoutMs = 30000,
  maxRetries = 3
): Promise<any> {
  const lastError: Error[] = [];
  let unauthorizedError: Error | null = null;
  const uploadMetadata: Record<string, any> = {};
  const fType = (file as any)?.type;
  if (fType && typeof fType === 'string') uploadMetadata.contentType = fType;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await new Promise<any>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file, uploadMetadata);
        const timeoutId = setTimeout(() => { uploadTask.cancel(); reject(new Error('Upload Timeout')); }, timeoutMs);
        uploadTask.on('state_changed',
          (snapshot) => { onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)); },
          (error) => { clearTimeout(timeoutId); reject(error); },
          async () => {
            clearTimeout(timeoutId);
            onProgress?.(100);
            try {
              const downloadUrl = await firebaseGetDownloadURL(uploadTask.snapshot.ref);
              resolve({ ref: uploadTask.snapshot.ref, metadata: uploadTask.snapshot.metadata, bytesTransferred: uploadTask.snapshot.bytesTransferred, totalBytes: uploadTask.snapshot.totalBytes, downloadUrl });
            } catch (urlErr) { reject(urlErr); }
          }
        );
      });
    } catch (error: any) {
      console.warn(`Firebase Storage upload (attempt ${attempt}/${maxRetries}) failed:`, error?.code || error?.message || error);
      lastError.push(error);
      if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as { code: string }).code;
        if (code === 'storage/unauthorized') {
          unauthorizedError = error;
          throw error;
        }
        if (code === 'storage/canceled') throw error;
      }
      // On QUIC protocol or network stall errors, try fallback uploadBytes on final attempt
      if (attempt === maxRetries - 1 && file instanceof File) {
        try {
          const snapshot = await uploadBytes(storageRef, file, uploadMetadata);
          onProgress?.(100);
          const downloadUrl = await firebaseGetDownloadURL(snapshot.ref);
          return { ref: snapshot.ref, metadata: snapshot.metadata, bytesTransferred: file.size, totalBytes: file.size, downloadUrl };
        } catch (fbFallbackErr: any) {
          console.warn('Fallback uploadBytes attempt failed:', fbFallbackErr?.message || fbFallbackErr);
        }
      }
      if (attempt < maxRetries) await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
  throw unauthorizedError || lastError[lastError.length - 1] || new Error('Upload failed after retries');
}

export async function uploadBytesWithTimeout(
  storageRef: any,
  file: Blob | Uint8Array | ArrayBuffer | File,
  timeoutMs = 30000,
  maxRetries = 3,
  onProgress?: (progress: number) => void
): Promise<any> {
  return uploadBytesResumableWithProgress(storageRef, file, onProgress, timeoutMs, maxRetries);
}

// Google Auth has been removed — PIN-based login is the sole method.

export async function generateNextApplicationId(): Promise<string> {
  const START_ID = 100020009;
  const counterRef = doc(db, 'counters', 'applicationId');
  try {
    const newId = await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(counterRef);
      const nextVal = docSnap.exists() ? ((docSnap.data().value || START_ID - 1) + 1) : START_ID;
      transaction.set(counterRef, { value: nextVal });
      return nextVal;
    });
    return String(newId);
  } catch (e) {
    // Under firestore.rules, counters writes are admin-only, meaning client-side
    // transactions by non-admin users will always fail and trigger this fallback.
    // A failure can also occur for admins due to transient issues (network, quota).
    // Falling back to a cryptographically random ID in the same range ensures
    // applicant creation is never blocked by rules or counter transaction hiccups.
    console.warn('generateNextApplicationId: using random fallback:', (e as any)?.code);
    const fallbackId = START_ID + (crypto.getRandomValues(new Uint32Array(1))[0] % 8999990);
    return String(fallbackId);
  }
}

/**
 * FIX (2026-06-25): Replaced `export enum OperationType` with a `const` object.
 *
 * TypeScript `export enum` compiles to an IIFE:
 *   var OperationType; (function(OperationType){...})(OperationType||(OperationType={}))\
 *
 * When Rollup/Vite bundles multiple modules that import from firebase.ts, the
 * module graph sorter can place a `const` binding (minified to a short name
 * like `J`) that references this enum value BEFORE the IIFE has executed.
 * This creates a Temporal Dead Zone (TDZ) violation at runtime:
 *   ReferenceError: Cannot access 'J' before initialization
 *
 * Using a plain `const` object makes all values immediately available as
 * simple property lookups — no IIFE, no ordering dependency, no TDZ risk.
 * The exported type `OperationTypeValue` preserves full TypeScript type safety
 * for all callers.
 */
export const OperationType = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  LIST: 'list',
  GET: 'get',
  WRITE: 'write',
} as const;

export type OperationTypeValue = typeof OperationType[keyof typeof OperationType];

export interface FirestoreErrorInfo {
  error: string; operationType: OperationTypeValue; path: string | null;
  authInfo: { userId?: string | null; email?: string | null; emailVerified?: boolean | null; isAnonymous?: boolean | null; tenantId?: string | null; providerInfo?: { providerId?: string | null; email?: string | null; }[]; };
}

export function handleFirestoreError(error: unknown, operationType: OperationTypeValue, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: { userId: auth.currentUser?.uid ?? null },
    operationType, path,
  };
  console.error('Firestore Error:', operationType, path, error instanceof Error ? error.message.slice(0, 120) : String(error).slice(0, 120));
  throw new Error(JSON.stringify(errInfo));
}

export async function createAuditLog(action: string, targetId: string, details?: string, bulkGroupId?: string): Promise<string | null> {
  try {
    const token = await auth.currentUser?.getIdToken();
    const body: any = { action, targetId, details };
    if (bulkGroupId) body.bulkGroupId = bulkGroupId;
    const res = await fetch('/api/audit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    if (!res.ok) { console.warn('Audit log API error:', await res.text()); return null; }
    const data = await res.json();
    return data.logId || null;
  } catch (e) { console.warn('Audit log API call failed:', e); return null; }
}
